'use client';

import React from 'react';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';

interface ProjectMetric {
  label: string;
  value: string;
  icon: string;
}

interface ProjectCardProps {
  title: string;
  category: string;
  industry: string;
  description: string;
  image: string;
  alt: string;
  metrics: ProjectMetric[];
  tags: string[];
  onViewDetails: () => void;
}

const ProjectCard: React.FC<ProjectCardProps> = ({
  title,
  category,
  industry,
  description,
  image,
  alt,
  metrics,
  tags,
  onViewDetails,
}) => {
  return (
    <div className="bg-card rounded-xl shadow-subtle hover:shadow-elevated transition-all duration-300 overflow-hidden group">
      <div className="relative h-56 overflow-hidden">
        <AppImage
          src={image}
          alt={alt}
          className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-500"
        />
        <div className="absolute top-4 left-4 flex gap-2">
          <span className="px-3 py-1 bg-primary text-primary-foreground text-xs font-semibold rounded-full">
            {category}
          </span>
          <span className="px-3 py-1 bg-secondary text-secondary-foreground text-xs font-semibold rounded-full">
            {industry}
          </span>
        </div>
      </div>

      <div className="p-6">
        <h3 className="font-heading font-bold text-xl text-text-primary mb-3 line-clamp-2">
          {title}
        </h3>
        <p className="text-text-secondary text-sm leading-relaxed mb-4 line-clamp-3">
          {description}
        </p>

        <div className="grid grid-cols-3 gap-3 mb-4">
          {metrics.map((metric, index) => (
            <div
              key={index}
              className="bg-muted rounded-lg p-3 text-center"
            >
              <Icon
                name={metric.icon as any}
                size={20}
                className="mx-auto mb-1 text-primary"
              />
              <div className="font-heading font-bold text-lg text-primary">
                {metric.value}
              </div>
              <div className="text-xs text-text-secondary">{metric.label}</div>
            </div>
          ))}
        </div>

        <div className="flex flex-wrap gap-2 mb-4">
          {tags.map((tag, index) => (
            <span
              key={index}
              className="px-2 py-1 bg-surface text-text-secondary text-xs rounded-md"
            >
              {tag}
            </span>
          ))}
        </div>

        <button
          onClick={onViewDetails}
          className="w-full px-4 py-2.5 bg-accent text-accent-foreground rounded-lg font-heading font-semibold text-sm hover:bg-accent/90 transition-all duration-300 flex items-center justify-center gap-2 group"
        >
          <span>View Case Study</span>
          <Icon
            name="ArrowRightIcon"
            size={16}
            className="transform group-hover:translate-x-1 transition-transform duration-300"
          />
        </button>
      </div>
    </div>
  );
};

export default ProjectCard;